<style lang="scss" scoped>
.groups {
	& /deep/ .el-select {
		width: 100%;
	}
	&__container {
		@include container;
	}
	&__panel {
		@include main-panel;
		& /deep/ .el-alert {
			padding: 14px;
		}
	}
	&__header {
		@include flex-center;
		&__options {
			margin-left: auto;
		}
	}
	&__footer {
		@include main-panel-footer;
		&__right {
			margin-left: auto;
		}
	}
	&__warning {
		color: red;
		font-size: 20px;
		padding-top: 7px;
	}
}
</style>
<template lang="pug">
.groups
	app-header
		.groups__header(slot='header')
			h1 Match groups
			.groups__header__options
				el-button(:type="saveButtonType", @click="saveMatches", :loading="saveLoading", :disabled='multipleMatches || excessiveCreateNows').ml-auto {{ saveText}}
	.groups__container
		.groups__panel
			loading(v-if="$apollo.loading")
			template(v-else)
				el-alert(v-if="groupSettings.group_create_mode === 'self'" type="warning" center show-icon :closable="false" title="Group creation is not enabled" description="To allow groups to be automatically created during a sync, see Settings > Advanced > Groups and group memberships")
				result-filter.ml-auto.mr-2(v-model="filters" :filter-fields='fields', :remember="true")
				el-table(:data="paginated").custom-table
					el-table-column(prop="name" label="Wonde group")    
						template(slot-scope="scope") 
							span {{ scope.row.name }}
					el-table-column(prop="type" label="Type")
						template(slot-scope="scope") 
							span {{ scope.row.type | capitalize }}
					el-table-column(width="50")
						template(slot-scope="scope")
							.groups__warning(v-if="rowDuplicated(scope.row)")
								i.fe.fe-alert-triangle
							.groups__warning(v-if="tooManyCreateNows(scope.row)")
								i.fe.fe-alert-triangle
					el-table-column(:label="`${providerName} Group`" width="500")
						template(slot-scope="scope")
							el-select(placeholder='Manage' filterable v-model="scope.row.assigned" @change='change')
								el-option-group
									el-option(key="ignore" value="ignore" label="Ignore")
									el-option(key="create_later" value="create_on_sync" label="Create on sync")
									el-option(key="create" value="create" label="Create now")
								el-option-group(:label="`Match to an existing ${providerName} group`")
									el-option(v-for="group in thirdPartyGroups" :key="group.id" :value="group.id" :label="group.name")
				.groups__footer
					.groups__footer__left
						el-pagination(background='', layout='prev, pager, next', :current-page.sync='page', :page-size='per_page', :total='filtered.length')
					.groups__footer__right                        
						| Showing
						strong &nbsp;{{ paginated.length }}
						| &nbsp;out of a possible
						strong &nbsp;{{ filtered.length }}
						| &nbsp;results

</template>
<script>
import gql from "graphql-tag";
import _ from "lodash";
export default {
	data() {
		return {
			filters: null,
			saveLoading: false,
			page: 1,
			per_page: 50,
			fields: [
				{
					name: "Name",
					path: "name",
					type: "text"
				},
				{
					name: "Type",
					path: "type",
					type: "radio",
					options: [
						{
							label: "House",
							value: "house"
						},
						{
							label: "Registration",
							value: "registration"
						},
						{
							label: "Year",
							value: "year"
						}
					]
				}
			]
		};
	},

	computed: {
		saveButtonType() {
			return this.multipleMatches ? "danger" : "primary";
		},

		saveText() {
			if (this.multipleMatches) {
				return "Saving is not possible while a group is assigned multiple times";
			}
			return this.saveLoading ? "Saving..." : "Save matches";
		},

		multipleMatches() {
			return _(this.inputGroups)
				.filter({ action: "match" })
				.reject(item => _.includes(["create_on_sync", "create", "ignore"], item.providerId))
				.countBy("providerId")
				.some(qty => qty > 1);
		},

		excessiveCreateNows() {
			return _.filter(this.inputGroups, { providerId: "create" }).length > 3;
		},

		inputGroups() {
			return _(this.groups)
				.reject({ assigned: null })
				.map(group => ({
					wondeId: group.id,
					providerId: group.assigned
				}))
				.value();
		},

		filtered() {
			let filtered = _.map(this.groups, row => {
				const foundRecord = _.find(this.matchedGroups, { wondeId: row.id });
				if (foundRecord) {
					row.assigned = foundRecord.providerId;
					row.status = foundRecord.status;
					if (foundRecord.status === "ignored") row.assigned = "ignore";
				} else {
					row.assigned = "create_on_sync";
				}
				return row;
			});

			if (this.groups && this.filters) {
				if (this.filters.name) {
					filtered = _.filter(filtered, row => _.startsWith(row.name.toLowerCase(), this.filters.name.toLowerCase()));
				}
				if (this.filters.type) {
					filtered = _.filter(filtered, { type: this.filters.type });
				}
			}
			return filtered;
		},

		paginated() {
			return _.chunk(this.filtered, this.per_page)[this.page - 1];
		}
	},

	methods: {
		rowDuplicated(item) {
			return (
				item.assigned &&
				_(this.inputGroups)
					.filter({ providerId: item.assigned })
					.reject(item => _.includes(["create_on_sync", "create", "ignore"], item.providerId))
					.value().length > 1
			);
		},

		tooManyCreateNows(item) {
			return item.assigned && this.excessiveCreateNows && item.assigned === "create";
		},

		change(id) {
			if (this.multipleMatches) {
				this.$message.warning("Notice: This group has been matched multiple times");
			}
			if (this.excessiveCreateNows) {
				this.$message.warning("Notice: Only 3 groups can be created at a time here - to create more you should set them to Create on sync");
			}
		},

		async saveMatches() {
			this.saveLoading = true;
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation MatchGroups($input: [MatchGroupInput!]!) {
							MatchGroups(input: $input) {
								id
								type
								name
							}
						}
					`,
					variables: {
						input: this.inputGroups
					}
				});
				await this.$apollo.queries.matchedGroups.refetch();
				await this.$apollo.queries.thirdPartyGroups.refetch();
				this.$message.success("Group matches saved successfully");
			} catch (error) {
				this.$message.error("Failed to save matches");
			}
			this.saveLoading = false;
		}
	},

	apollo: {
		matchedGroups: {
			query: gql`
				{
					MatchedGroups {
						id
						status
						providerId
						wondeId
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r => r.MatchedGroups
		},

		thirdPartyGroups: {
			query: gql`
				{
					ThirdPartyGroups {
						id
						name
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r => r.ThirdPartyGroups
		},

		groups: {
			query: gql`
				{
					Groups {
						id
						name
						type
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r =>
				_(r.Groups)
					.map(group => ({
						...group,
						assigned: null
					}))
					.sortBy(["type", "name"])
					.value()
		},

		groupSettings: {
			query: gql`
				{
					Settings {
						id
						group_create_mode
						group_membership_mode
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update(r) {
				return _.cloneDeep(r.Settings);
			}
		}
	}
};
</script>
